import * as React from 'react';
import clsx from 'clsx';
import { useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Asterisk, CheckIcon } from 'lucide-react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { InputProps, Option } from '@/interfaces';
import { InputText } from '../input-text';
import { useForm } from 'react-hook-form';
import Icons from '@/const/icons';
import { ErrorMessageInput, Icon } from '@/components/common';
import Image from 'next/image';

export interface InputListMultipleHandle {
	reset: () => void;
}
interface Props extends InputProps {
	title?: string;
	className?: string;
	classNameInput?: string;
	classNameIcon?: string;
	markAsRequired?: boolean;
	placeholder?: string;
	options: Option[];
	isSearch?: boolean;
	classNameDropdownContainer?: string;
	optionIconClassName?: string;
	classNameSearch?: string;
	classNameTitle?: string;
	selectedClassName?: string;
	myDefaultValues?: string[];
	withIcons?: boolean;
	iconIsImage?: boolean;
	maxSelectedDisplay?: number;
	onSelectAll?: (selected: boolean) => void;
	allSelected?: boolean;
}

export const InputListMultiple = React.forwardRef<
	InputListMultipleHandle,
	Props
>(
	(
		{
			title,
			options,
			className,
			placeholder,
			markAsRequired,
			setValueInput,
			classNameInput,
			isSearch,
			myDefaultValues,
			classNameSearch,
			selectedClassName,
			classNameDropdownContainer,
			iconIsImage,
			optionIconClassName,
			classNameTitle,
			classNameIcon,
			withIcons,
			maxSelectedDisplay = 5,
			onSelectAll,
			allSelected,
			...props
		},
		ref
	) => {
		const [selected, setSelected] = useState<Option[]>([]);
		const [isMounted, setIsMounted] = useState<boolean>(false);
		const [filter, setFilter] = useState('');
		const { register } = useForm();

		React.useEffect(() => {
			setValueInput && setValueInput(props.name, selected);
		}, [selected]);

		React.useEffect(() => {
			const defaultValue =
				options.filter((opt) => myDefaultValues?.includes(opt.name)) || options;

			if (defaultValue && defaultValue?.length > 0 && !isMounted) {
				setSelected(defaultValue);
				setIsMounted(true);
			}
		}, [myDefaultValues, options, isMounted]);

		const filteredOptions = options.filter((option) =>
			(option.label as string).toLowerCase().includes(filter.toLowerCase())
		);
		React.useImperativeHandle(ref, () => ({
			reset: () => {
				setSelected([]);
			},
		}));

		const handleSelectAll = () => {
			if (onSelectAll) {
				onSelectAll(!allSelected);
				if (!allSelected) {
					setSelected(options.filter((opt) => opt.name !== 'all'));
				} else {
					setSelected([]);
				}
			}
		};

		return (
			<div className={clsx(className)}>
				<input
					type="text"
					value={selected.map((item) => item.name)}
					className="hidden"
					readOnly
					{...props.register(props.name, props.rules)}
				/>
				<Listbox
					value={selected}
					onChange={(value) => {
						if (value.some((v) => v.name === 'all')) {
							handleSelectAll();
						} else {
							setSelected(value);
						}
					}}
					multiple
				>
					{({ open }) => (
						<>
							<Listbox.Label className="block text-left">
								<label
									htmlFor="name"
									className={
										(clsx('text-sm 2xl:text-base text-black relative'),
										classNameTitle)
									}
								>
									{title}
									{markAsRequired && (
										<Asterisk className="w-3 h-3 absolute -right-3 top-0 text-red-500" />
									)}
								</label>
							</Listbox.Label>
							<div
								className={clsx('relative max-w-full group', {
									'!cursor-not-allowed': props.disabled,
								})}
							>
								<Listbox.Button
									className={clsx(
										'mt-[5px] relative w-full text-left border border-opacity-primary flex focus:outline-none bg-white truncate overflow-hidden rounded-full px-6 py-3',
										{ '!border-main-red': props.error },
										{ 'cursor-pointer': !props.disabled },
										{
											'!border-main-red': props.error,
											'cursor-not-allowed pointer-events-none group-hover:!bg-[#F4F4F4A3] text-opacity-primary':
												props.disabled,
										},
										classNameInput
									)}
								>
									<div className="truncate line-clamp-1 max-w-full text-base">
										{selected.length > 0 && (
											<div className="flex items-center gap-x-[10px]">
												{selected
													.slice(0, maxSelectedDisplay)
													.map((item, index) => (
														<span
															className={clsx(
																'flex items-center gap-x-2',
																selectedClassName
															)}
															key={index}
														>
															{withIcons && (
																<Icon
																	width={20}
																	height={20}
																	src={item.icon || ''}
																	className={item.classNameItemIcon}
																/>
															)}
															{item.label}
															{index < selected.length - 1 &&
																!withIcons &&
																', '}
														</span>
													))}
												{selected.length > maxSelectedDisplay &&
													`, +${selected.length - maxSelectedDisplay}`}
											</div>
										)}
									</div>
									{selected.length === 0 && (
										<span className="flex items-center">
											<span
												className={clsx(
													'block truncate text-base  text-opacity-primary',
													{
														'text-opacity-primary': props.disabled,
													}
												)}
											>
												{placeholder || 'Select'}
											</span>
										</span>
									)}

									<span
										className={clsx(
											'absolute inset-y-0 right-5 flex items-center ml-3 pointer-events-none'
										)}
									>
										{open ? (
											<ChevronUpIcon
												width={20}
												height={20}
												className="text-black"
											/>
										) : (
											<ChevronDownIcon
												width={20}
												height={20}
												className="text-black"
											/>
										)}
									</span>
								</Listbox.Button>
								<Transition
									show={open}
									as={React.Fragment}
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
									afterLeave={() => setFilter('')}
								>
									<Listbox.Options
										static
										className={clsx(
											'py-4',
											'absolute mt-1 z-30 w-full bg-white rounded-lg border border-opacity-primary  focus:outline-none shadow-lg overflow-hidden scroll-custom',
											classNameDropdownContainer
										)}
									>
										<InputText
											name="search"
											register={register}
											placeholder="Search"
											iconLeft={Icons.search}
											className={clsx(' left-0 px-4 w-full', classNameSearch)}
											classNameIcon={clsx(
												'!top-[40%] !w-4 !h-4',
												classNameIcon
											)}
											classNameInput={clsx(
												'pl-9 border-none !bg-gray-15 h-8 !mt-0',
												classNameInput
											)}
											onChange={({ target }) => setFilter(target.value)}
										/>
										<div
											className={clsx(
												'overflow-auto scroll-custom max-h-72 !rounded-none',
												{
													'mt-3': isSearch,
												}
											)}
										>
											{filteredOptions?.map((option, idx) => (
												<Listbox.Option
													key={option.name + idx}
													value={option}
													className={({ selected }) =>
														clsx(
															selected && 'bg-dark-10',
															'relative px-4 py-3 cursor-pointer select-none capitalize'
														)
													}
												>
													{({ selected }) => (
														<div className="flex items-center gap-2">
															<span className="pr-4 w-6 h-6 rounded border border-[#0D061633]">
																{(selected ||
																	(option.name === 'all' && allSelected)) && (
																	<CheckIcon
																		className="w-5 h-5 text-green-60 ml-[0.1rem]"
																		aria-hidden="true"
																	/>
																)}
															</span>
															{option.icon &&
																(iconIsImage ? (
																	<Image
																		src={option.icon}
																		width={16}
																		height={16}
																		className={clsx(
																			'size-4',
																			optionIconClassName,
																			option.classNameItemIcon
																		)}
																		alt="option-icon"
																		unoptimized
																	/>
																) : (
																	<Icon
																		src={option.icon}
																		className={clsx(
																			'!size-4',
																			optionIconClassName,
																			option.classNameItemIcon
																		)}
																	/>
																))}
															<span className="block truncate text-sm 2xl:text-base text-black">
																{option.label}
															</span>
														</div>
													)}
												</Listbox.Option>
											))}
										</div>
									</Listbox.Options>
								</Transition>
							</div>
						</>
					)}
				</Listbox>
				{props.error && props.error.message && (
					<ErrorMessageInput message={props.error.message} />
				)}
			</div>
		);
	}
);
InputListMultiple.displayName = 'InputListMultiple';
